import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ElLaItem } from "@/lib/types";
import { useProfileStore } from "@/state/profile";
import { useSessionStore } from "@/state/session";
import { GameShell } from "@/components/GameShell";
import { FeedbackOverlay } from "@/components/FeedbackOverlay";
import { burstConfetti, sfxCorrect, sfxWrong } from "@/lib/rewards";
import { gradeLocally } from "@/lib/grade";
import { cn } from "@/lib/cn";

export function ElLaGame() {
  return (
    <>
      <GameShell game="el_la">
        {({ item, disabled }) => {
          if (item.kind !== "el_la") return null;
          return <ElLaCard item={item} disabled={disabled} />;
        }}
      </GameShell>
      <ElLaFeedback />
    </>
  );
}

function ElLaCard({ item, disabled }: { item: ElLaItem; disabled: boolean }) {
  const profileId = useProfileStore((s) => s.activeId);
  const answer = useSessionStore((s) => s.answer);
  const [picked, setPicked] = useState<"el" | "la" | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    setPicked(null);
    setInfoOpen(false);
  }, [item.id]);

  async function choose(article: "el" | "la") {
    if (disabled || picked) return;
    setPicked(article);
    // Fire feedback immediately from the local grade — server is authoritative
    // but its RTT would introduce noticeable lag on slow connections / cold AI.
    const { correct } = gradeLocally(item, article);
    if (correct) {
      sfxCorrect((useSessionStore.getState().loaded?.combo ?? 0) + 1);
      burstConfetti();
    } else {
      sfxWrong();
    }
    const res = await answer(profileId, article);
    if (!res) setPicked(null);
  }

  return (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-full flex flex-col items-center justify-center pt-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="pill">Sustantivo · {diffLabel(item.difficulty)}</span>
        <button
          aria-label="Show meaning"
          onClick={() => setInfoOpen((v) => !v)}
          className="h-9 w-9 rounded-full bg-brand-surface border border-brand-border shadow-chunk grid place-items-center font-black text-brand-sky"
        >
          i
        </button>
      </div>

      <motion.div
        key={item.lemma}
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="chunk px-6 py-10 w-full max-w-md text-center"
      >
        <div className="font-display text-5xl">{item.lemma}</div>
        <AnimatePresence>
          {infoOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 text-brand-muted font-bold">
                {item.english}
              </div>
              {item.note && (
                <p className="mt-2 text-sm text-brand-ink bg-brand-saffron/15 rounded-2xl px-3 py-2">
                  {item.note}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <div className="w-full max-w-md mt-8 grid grid-cols-2 gap-3">
        <ArticleButton
          article="el"
          active={picked === "el"}
          disabled={disabled}
          onClick={() => choose("el")}
        />
        <ArticleButton
          article="la"
          active={picked === "la"}
          disabled={disabled}
          onClick={() => choose("la")}
        />
      </div>
    </motion.div>
  );
}

function ArticleButton({
  article,
  active,
  disabled,
  onClick,
}: {
  article: "el" | "la";
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "chunk-btn text-3xl font-display py-6 border",
        article === "el"
          ? "bg-brand-sky/10 border-brand-sky/40 text-brand-sky"
          : "bg-brand-rose/10 border-brand-rose/40 text-brand-rose",
        active && "ring-4 ring-offset-2 ring-brand-ink/20 scale-[1.02]",
        disabled && "opacity-60",
      )}
    >
      {article}
    </button>
  );
}

function ElLaFeedback() {
  const loaded = useSessionStore((s) => s.loaded);
  const advance = useSessionStore((s) => s.advance);
  return (
    <FeedbackOverlay result={loaded?.lastResult ?? null} onNext={advance} />
  );
}

function diffLabel(d: 1 | 2 | 3): string {
  return d === 1 ? "Fácil" : d === 2 ? "Medio" : "Difícil";
}
